import {Component, inject, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faArrowRight, faCircleExclamation, faSpinner, faXmark} from '@fortawesome/pro-solid-svg-icons';
import {BackgroundProgress} from '../../../../services/background-progress.model';
import {ClassificationService} from '../../../../services/classification/classification.service';
import {DeleteService} from '../../../../services/delete/delete.service';
import {
  ReclassificationProgressService
} from '../../../../services/reclassification/reclassification-progress.service';
import {SourceLinkSyncService} from '../../../../services/source-link/source-link-sync.service';
import {StateConfigService} from '../../../../services/state/state-config.service';
import {UploadService} from '../../../../services/upload/upload.service';

/** Ein Vorgang IN EINEM PROJEKT, den die Badge anzeigt. */
interface BackgroundActivity {
  /** Stabiler Schlüssel für `@for track`: Vorgangsart und Projekt zusammen. */
  key: string;
  /** Überschrift im Panel. */
  title: string;
  /**
   * Projektname unter der Überschrift — nur gesetzt, wenn überhaupt mehr als ein Projekt beteiligt
   * ist. Im Normalfall (alles im selben Projekt) wäre die Zeile nur Lärm.
   */
  projectName?: string;
  progress: BackgroundProgress;
  /**
   * Was ein Neuladen der Seite mit diesem Vorgang macht — und das unterscheidet sich:
   * Upload und Löschen halten noch wartende Einheiten im Tab, die Klassifizierung läuft
   * ausschließlich serverseitig und ist von einem Reload gar nicht betroffen.
   */
  survivesReload: boolean;
}

/**
 * Schwebende Anzeige für Vorgänge, die im Hintergrund weiterlaufen — am rechten Rand neben chatBIC
 * und dem bugBIC-Feedback-Tab.
 *
 * <p>Warum es das braucht: `UploadService` und `DeleteService` sind beide `providedIn: 'root'` und
 * arbeiten beim Navigieren weiter — man sah es nur nirgends. Wer das Fortschritts-Popup schloss
 * („im Hintergrund weiterlaufen lassen") und die Dateiansicht verließ, hatte keine Stelle mehr, an
 * der sichtbar war, ob noch etwas läuft, wie weit es ist oder ob etwas fehlgeschlagen ist. Für
 * Uploads galt dasselbe: `bic-file-upload-progress` hängt ausschließlich in der Dateiansicht.</p>
 *
 * <p>Bewusst nur sichtbar, solange etwas läuft (oder ein Fehler noch nicht quittiert wurde): der
 * rechte Rand ist bereits zwischen chatBIC und bugBIC aufgeteilt, ein dritter Dauergast würde ihn
 * zumüllen. Fehler bleiben dagegen stehen, bis man sie wegklickt — `clearFinished()` räumt sie
 * sonst mit weg, und dann erfährt sie niemand mehr.</p>
 *
 * <p>Die Anzeige liest ausschließlich die `progress$`-Sichten der beiden Services. Wandert die
 * Lösch-Warteschlange auf den Server, ändert sich nur diese Quelle, nicht diese Komponente.</p>
 */
@Component({
  selector: 'app-background-activity-badge',
  templateUrl: './background-activity-badge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent],
})
export class BackgroundActivityBadgeComponent implements OnDestroy {
  /**
   * Fortschritt je Vorgangsart, darin EIN Eintrag JE PROJEKT.
   *
   * Die Warteschlangen der Services sind tabweit: wer in Projekt A hochlädt, das Projekt wechselt
   * und in B weitermacht, hat zwei Uploads gleichzeitig offen. Eine gemeinsame Zeile hätte deren
   * Zähler addiert und beim Klick kein eindeutiges Ziel gehabt — deshalb zeigt die Badge sie als
   * getrennte Abschnitte.
   */
  uploadByProject: BackgroundProgress[] = [];
  classifyByProject: BackgroundProgress[] = [];
  reclassifyByProject: BackgroundProgress[] = [];
  deleteByProject: BackgroundProgress[] = [];
  syncByProject: BackgroundProgress[] = [];

  /**
   * Projekt, in dessen Dateiansicht gerade navigiert wird — oder null.
   *
   * Der Sprung dauert mehrere Sekunden (Projekt laden, dann die Dateiliste), und in dieser Zeit
   * passierte sichtbar nichts: der Klick sah aus, als wäre er ins Leere gegangen. Solange das
   * gesetzt ist, zeigt der Abschnitt einen Spinner, bleibt das Panel offen und weitere Klicks
   * werden verworfen.
   */
  navigatingProjectId: number | null = null;
  /** Das Panel wollte während der Navigation zugehen — nachgeholt, sobald sie durch ist. */
  private closeAfterNavigation = false;

  /** Panel-Sichtbarkeit, per Hover. */
  panelOpen = false;
  /**
   * Nachlauf beim Verlassen, bevor das Panel zugeht.
   *
   * Ohne ihn reisst jede kurze Unterbrechung der Hover-Kette das Panel weg - und weil es per
   * `@if` aus dem DOM faellt, ist es dann endgueltig weg, nicht nur unsichtbar. Der Nachlauf
   * ueberbrueckt die diagonale Mausbewegung ueber die Ecke des Reiters hinweg.
   */
  private static readonly PANEL_CLOSE_DELAY_MS = 250;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Gesetzt, wenn der Nutzer fehlgeschlagene Vorgänge zur Kenntnis genommen hat. */
  errorsDismissed = false;

  protected readonly faSpinner = faSpinner;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faXmark = faXmark;
  protected readonly faArrowRight = faArrowRight;

  private readonly deleteService = inject(DeleteService);
  private readonly uploadService = inject(UploadService);
  private readonly classificationService = inject(ClassificationService);
  private readonly reclassificationService = inject(ReclassificationProgressService);
  private readonly sourceLinkSyncService = inject(SourceLinkSyncService);
  private readonly stateConfigService = inject(StateConfigService);
  private readonly router = inject(Router);
  private readonly subs = new Subscription();

  constructor() {
    this.subs.add(this.uploadService.progressByProject$.subscribe(progress => {
      this.uploadByProject = progress;
      this.resetDismissOnNewWork();
    }));
    this.subs.add(this.classificationService.progressByProject$.subscribe(progress => {
      this.classifyByProject = progress;
      this.resetDismissOnNewWork();
    }));
    this.subs.add(this.reclassificationService.progressByProject$.subscribe(progress => {
      this.reclassifyByProject = progress;
      this.resetDismissOnNewWork();
    }));
    this.subs.add(this.deleteService.progressByProject$.subscribe(progress => {
      this.deleteByProject = progress;
      this.resetDismissOnNewWork();
    }));
    this.subs.add(this.sourceLinkSyncService.progressByProject$.subscribe(progress => {
      this.syncByProject = progress;
      this.resetDismissOnNewWork();
    }));
  }

  /**
   * Die Vorgänge, die gerade etwas zu zeigen haben — feste Reihenfolge, damit nichts springt: erst
   * nach Vorgangsart, darin nach Projekt-ID. Die Gruppierung selbst liefert keine stabile
   * Reihenfolge, die Abschnitte tauschten sonst bei jeder Meldung die Plätze.
   */
  get activities(): BackgroundActivity[] {
    const kinds = [
      // Der Upload haelt die Bytes im Browser, das Loeschen seit dem Server-Batch nur noch die
      // Anzeige - die Warnung bleibt dort trotzdem, weil ein Reload den Dialog-Fortschritt kappt.
      {kind: 'upload', title: 'Dateien hochladen', list: this.uploadByProject, survivesReload: false},
      {kind: 'classify', title: 'Dateien klassifizieren', list: this.classifyByProject, survivesReload: true},
      // Der Lauf aus der Dokumentenklassen-Verwaltung. Eigene Quelle, weil der Server den Stapel
      // dort selbst anlegt und der ClassificationService ihn nie zu sehen bekommt.
      {kind: 'reclassify', title: 'Dateien neu klassifizieren', list: this.reclassifyByProject, survivesReload: true},
      {kind: 'delete', title: 'Dateien löschen', list: this.deleteByProject, survivesReload: false},
      // Der SharePoint-Abgleich läuft ganz auf dem Server, ein Neuladen stört ihn nicht.
      {kind: 'sync', title: 'Mit SharePoint abgleichen', list: this.syncByProject, survivesReload: true},
    ];

    const relevant = kinds.flatMap(kind => kind.list
      .filter(progress => progress.active > 0 || progress.failed > 0)
      .sort((a, b) => (a.projectId ?? 0) - (b.projectId ?? 0))
      .map(progress => ({kind, progress})));

    const multipleProjects = new Set(relevant.map(a => a.progress.projectId)).size > 1;

    return relevant.map(({kind, progress}) => ({
      key: kind.kind + '-' + progress.projectId,
      title: kind.title,
      projectName: multipleProjects ? this.projectName(progress.projectId) : undefined,
      progress,
      survivesReload: kind.survivesReload,
    }));
  }

  get running(): boolean {
    return this.allProgress.some(progress => progress.active > 0);
  }

  get failed(): number {
    return this.allProgress.reduce((sum, progress) => sum + progress.failed, 0);
  }

  /** Sichtbar solange etwas läuft, danach nur noch bei unquittierten Fehlern. */
  get visible(): boolean {
    if (this.running) return true;
    return this.failed > 0 && !this.errorsDismissed;
  }

  /** Abgeschlossene Einheiten inkl. Fehlschläge — der Fortschritt kommt nicht zum Stillstand. */
  settled(progress: BackgroundProgress): number {
    return progress.done + progress.failed;
  }

  percent(progress: BackgroundProgress): number {
    if (progress.total === 0) return 0;
    return Math.round((this.settled(progress) / progress.total) * 100);
  }

  /**
   * Sehr kurzer Text, der im Reiter erst beim Hover ausfährt.
   *
   * <p>Eingeklappt zeigt der Reiter bewusst NUR das Icon, damit er genauso breit ist wie die
   * chatBIC- und Feedback-Reiter daneben — drei Tabs unterschiedlicher Breite am selben Rand sehen
   * nach Versehen aus.</p>
   */
  get tabLabel(): string {
    if (this.running) {
      // Ueber die ANGEZEIGTEN Vorgaenge, nicht ueber alles, was die Services fuehren. Sonst zaehlt
      // der Reiter auch abgeschlossene Gruppen mit, deren Abschnitt laengst ausgeblendet ist: im
      // Bericht vom 2026-09-17 stand am Reiter 849/853, waehrend das Panel darunter 649 von 653
      // zeigte - die Differenz war ein fertiger Stapel aus 200 Dateien.
      const shown = this.activities.map(activity => activity.progress);
      const settled = shown.reduce((sum, progress) => sum + this.settled(progress), 0);
      const total = shown.reduce((sum, progress) => sum + progress.total, 0);
      return total > 1 ? `${settled}/${total}` : 'Läuft';
    }
    return this.failed === 1 ? '1 Fehler' : `${this.failed} Fehler`;
  }

  /** Ausformulierter Titel im Panel. */
  get label(): string {
    if (this.running) {
      return this.activities.length > 1 ? 'Vorgänge laufen' : this.activities[0]?.title ?? 'Vorgang läuft';
    }
    return this.failed === 1
      ? '1 Datei konnte nicht verarbeitet werden'
      : `${this.failed} Dateien konnten nicht verarbeitet werden`;
  }

  /** Hover auf Reiter oder Panel: oeffnen und ein laufendes Schliessen abbrechen. */
  openPanel(): void {
    this.closeAfterNavigation = false;
    this.cancelClose();
    this.panelOpen = true;
  }

  /** Verlassen: erst nach kurzem Nachlauf schliessen (s. PANEL_CLOSE_DELAY_MS). */
  schedulePanelClose(): void {
    // Waehrend der Navigation bleibt das Panel stehen, sonst waere der Spinner weg, sobald man die
    // Maus wegnimmt. Nachgeholt wird das Schliessen in finishNavigation().
    if (this.navigatingProjectId !== null) {
      this.closeAfterNavigation = true;
      return;
    }
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.panelOpen = false;
    }, BackgroundActivityBadgeComponent.PANEL_CLOSE_DELAY_MS);
  }

  dismiss(): void {
    this.errorsDismissed = true;
    this.closePanel();
  }

  /**
   * Springt in die Dateiansicht des Projekts, zu dem der Vorgang gehört.
   *
   * Warum nicht einfach das ausgewählte Projekt: Upload und Löschen laufen beim Projektwechsel
   * weiter, die Badge kann also über Arbeit aus einem anderen Projekt stehen. `progress.projectId`
   * trägt das Projekt der noch offenen Arbeit mit; nur wenn keine Quelle es kennt (etwa ein
   * Klassifizierungs-Stapel ohne Zuordnung), bleibt das ausgewählte Projekt das Ziel.
   *
   * Der Projektwechsel selbst passiert im `ProjectResolver` der Route — hier ist nichts zu tun.
   */
  openFiles(activity?: BackgroundActivity): void {
    // Zweiter Klick waehrend der Navigation: verwerfen. Genau dafuer ist der Spinner da.
    if (this.navigatingProjectId !== null) return;

    // Ohne Vorgang (Klick auf den Reiter selbst) zaehlt der erste Vorgang, der ein Projekt kennt.
    const projectId = activity?.progress.projectId
      ?? this.activities.find(a => a.progress.projectId)?.progress.projectId
      ?? this.currentProjectId;
    if (!projectId) return;

    // Panel offen lassen und den Spinner zeigen, statt sofort zu schliessen: bis die Dateiansicht
    // steht, vergehen mehrere Sekunden, und ohne Rueckmeldung klickt man in der Zwischenzeit
    // weiter.
    this.cancelClose();
    this.navigatingProjectId = projectId;

    // Das Versprechen loest mit dem Ende der Navigation auf (also nach den Resolvern). Das Laden
    // der Dateiliste selbst zeigt danach die Dateiansicht mit ihrem eigenen Ladezustand an.
    void this.router.navigate(['/projects', projectId, 'files'])
      .catch(() => undefined)
      .then(() => this.finishNavigation());
  }

  private get currentProjectId(): number {
    return this.stateConfigService.getProjectId();
  }

  /** Alles, was die drei Services melden — über Vorgangsarten UND Projekte hinweg. */
  private get allProgress(): BackgroundProgress[] {
    return [
      ...this.uploadByProject, ...this.classifyByProject,
      ...this.reclassifyByProject, ...this.deleteByProject, ...this.syncByProject,
    ];
  }

  /**
   * Name für die Projektzeile. `StateConfigService.projects` kennt jedes Projekt, in dem der Nutzer
   * eine Rolle hat (`RoleResponse.name` ist dort der Projektname) — die ID als Notnagel, damit zwei
   * Abschnitte auch dann unterscheidbar bleiben, wenn der Name noch nicht geladen ist.
   */
  private projectName(projectId?: number): string {
    if (!projectId) return 'Unbekanntes Projekt';
    return this.stateConfigService.projects.get(projectId)?.name ?? 'Projekt ' + projectId;
  }

  ngOnDestroy(): void {
    this.cancelClose();
    this.subs.unsubscribe();
  }

  private closePanel(): void {
    this.cancelClose();
    this.panelOpen = false;
  }

  /** Navigation durch: Spinner weg, und ein waehrenddessen unterdruecktes Schliessen nachholen. */
  private finishNavigation(): void {
    this.navigatingProjectId = null;
    if (!this.closeAfterNavigation) return;
    this.closeAfterNavigation = false;
    this.closePanel();
  }

  private cancelClose(): void {
    if (this.closeTimer === null) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  /** Neue Arbeit hebt eine früher quittierte Fehlermeldung auf. */
  private resetDismissOnNewWork(): void {
    if (this.running) {
      this.errorsDismissed = false;
    }
  }
}
